import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { getAllCollectionsAsync, getCollectionByIdAsync, updateInventoryStructure, setAllItemsNotAvailable } from '../utils/inventoryService';
import InventoryEditor from './InventoryEditor';
import UserManagement from './UserManagement';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const AdminContainer = styled.div`
  padding: 40px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #ccc;
  font-size: 1.2rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #ff6b6b;
  font-size: 1rem;
  gap: 15px;
`;

const RetryButton = styled.button`
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #555;
  }
`;

const AdminHeader = styled.h1`
  font-size: 2rem;
  margin-bottom: 20px;
`;

const CollectionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

const CollectionCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CollectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 10px;
`;

const CollectionStats = styled.div`
  display: flex;
  gap: 20px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatLabel = styled.span`
  font-size: 0.8rem;
  color: #aaa;
`;

const StatValue = styled.span`
  font-size: 1.1rem;
  font-weight: bold;
`;

const AdminControls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const AdminButton = styled.button`
  background: transparent;
  border: 1px solid #aaa;
  color: #fff;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ResetButton = styled(AdminButton)`
  border-color: #a00;
  color: #a00;

  &:hover {
    background: rgba(170, 0, 0, 0.1);
  }
`;

const UploadButton = styled(AdminButton)`
  border-color: #2c7d46;
  color: #2c7d46;

  &:hover {
    background: rgba(44, 125, 70, 0.1);
  }
`;

const UploadModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const UploadModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const UploadModalHeader = styled.h2`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
`;

const UploadForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #aaa;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #555;
  color: #fff;
  padding: 10px;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #2c7d46;
  }
`;

const Select = styled.select`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #555;
  color: #fff;
  padding: 10px;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #2c7d46;
  }

  option {
    background: #1a1a1a;
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
  border: 2px dashed #555;
  border-radius: 4px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2c7d46;
    background: rgba(44, 125, 70, 0.05);
  }

  ${props => props.isDragging && `
    border-color: #2c7d46;
    background: rgba(44, 125, 70, 0.1);
  `}
`;

const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 150px;
  overflow-y: auto;
  margin-top: 10px;
`;

const FileItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
`;

const RemoveFileButton = styled.button`
  background: rgba(170, 0, 0, 0.2);
  border: none;
  color: #ff6b6b;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    background: rgba(170, 0, 0, 0.4);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 10px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #2c7d46;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const StatusMessage = styled.div`
  margin-top: 10px;
  padding: 10px;
  border-radius: 4px;
  font-size: 0.9rem;
  ${props => props.type === 'success' && `
    background: rgba(44, 125, 70, 0.2);
    color: #4caf50;
  `}
  ${props => props.type === 'error' && `
    background: rgba(170, 0, 0, 0.2);
    color: #ff6b6b;
  `}
`;

const Notification = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 20px;
  border-radius: 4px;
  font-size: 0.9rem;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  ${props => props.type === 'success' && `
    background: rgba(44, 125, 70, 0.9);
    color: #fff;
    border: 1px solid #2c7d46;
  `}
  ${props => props.type === 'error' && `
    background: rgba(170, 0, 0, 0.9);
    color: #fff;
    border: 1px solid #a00;
  `}
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 1000;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #333;
  }

  ${TooltipWrapper}:hover & {
    opacity: 1;
  }
`;

const HamburgerMenuWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const HamburgerButton = styled.button`
  background: transparent;
  border: 1px solid #aaa;
  color: #fff;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  span {
    display: block;
    width: 20px;
    height: 2px;
    background: #fff;
    transition: all 0.2s ease;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #1a1a1a;
  border: 1px solid #555;
  border-radius: 4px;
  min-width: 220px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
`;

const MenuItem = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  color: #fff;
  padding: 12px 16px;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
  font-size: 0.9rem;
  border-bottom: 1px solid #333;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  ${props => props.danger && `
    color: #ff6b6b;

    &:hover {
      background: rgba(170, 0, 0, 0.1);
    }
  `}
`;

function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collections, setCollections] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [selectedCollectionForUpload, setSelectedCollectionForUpload] = useState('');
  const [newCollectionName, setNewCollectionName] = useState('');
  const [isNewCollection, setIsNewCollection] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [notification, setNotification] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);

  // Load collections from the inventory service
  useEffect(() => {
    let mounted = true;

    const loadCollections = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 Admin useEffect: Loading collections (trigger:', refreshTrigger, ')');

        // Load from server
        const allCollections = await getAllCollectionsAsync();
        console.log('📦 Admin useEffect: Loaded', allCollections.length, 'collections');

        // Log availability stats
        if (allCollections.length > 0) {
          const totalItems = allCollections.reduce((sum, col) => sum + (col.items?.length || 0), 0);
          const availableItems = allCollections.reduce((sum, col) =>
            sum + (col.items?.filter(item => item.available).length || 0), 0);
          console.log(`📊 Admin useEffect: ${availableItems} of ${totalItems} total items are available`);
        }

        if (mounted) {
          setCollections(allCollections);
          console.log('✅ Admin useEffect: Collections state updated');
        }
      } catch (err) {
        console.error('❌ Error loading collections:', err);
        if (mounted) {
          setError('Failed to load collections. Please try again.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadCollections();

    return () => {
      mounted = false;
    };
  }, [refreshTrigger]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest('[data-menu]')) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Calculate available and total items for each collection
  const collectionsWithStats = collections.map(collection => {
    const totalItems = collection.items ? collection.items.length : 0;
    const availableItems = collection.items
      ? collection.items.filter(item => item.available).length
      : 0;

    return {
      ...collection,
      totalItems,
      availableItems
    };
  });

  // Handle updating inventory structure
  const handleUpdateStructure = async () => {
    try {
      console.log('Calling updateInventoryStructure...');
      const result = await updateInventoryStructure();
      console.log('updateInventoryStructure completed with result:', result);

      // Show notification based on result
      if (result.success) {
        if (result.newCollections && result.newCollections.length > 0) {
          setNotification({
            type: 'success',
            message: `Added ${result.newCollections.length} new collection(s): ${result.newCollections.join(', ')}`
          });
        } else {
          setNotification({
            type: 'success',
            message: 'Inventory structure updated successfully!'
          });
        }
      } else {
        setNotification({
          type: 'error',
          message: result.error || 'Failed to update inventory structure'
        });
      }

      // Auto-hide notification after 10 seconds
      setTimeout(() => setNotification(null), 10000);

      // Force a refresh to show the new collections
      console.log('Triggering UI refresh...');
      setRefreshTrigger(Date.now());
    } catch (error) {
      console.error('Error updating inventory structure:', error);
      setNotification({
        type: 'error',
        message: 'An error occurred while updating the inventory structure'
      });
      setTimeout(() => setNotification(null), 10000);
    }
  };

  // Handle setting all items as not available
  const handleSetNotAvailable = async () => {
    // Browser confirmation dialog
    const confirmed = window.confirm(
      'This will set ALL items in ALL collections as NOT AVAILABLE.\n\n' +
      'This action will update the server metadata for every collection.\n\n' +
      'Are you sure you want to continue?'
    );

    if (!confirmed) {
      return;
    }

    setMenuOpen(false);

    try {
      // Show notification that process is starting
      setNotification({
        type: 'success',
        message: 'Updating all items... This may take a moment.'
      });

      const success = await setAllItemsNotAvailable();

      if (success) {
        setNotification({
          type: 'success',
          message: 'Successfully set all items as not available!'
        });

        // Wait a brief moment to ensure cache is cleared, then refresh
        setTimeout(() => {
          setRefreshTrigger(Date.now());
        }, 100);
      } else {
        setNotification({
          type: 'error',
          message: 'Failed to update items. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error setting items as not available:', error);
      setNotification({
        type: 'error',
        message: 'An error occurred while updating items.'
      });
    }

    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  const handleCollectionClick = async (collection) => {
    // Reload the collection from server to get latest data
    console.log(`🔍 Reloading collection ${collection.id} from server...`);
    const freshCollection = await getCollectionByIdAsync(collection.id);
    if (freshCollection) {
      console.log(`✅ Loaded fresh data for ${collection.id}, first item available:`, freshCollection.items?.[0]?.available);
      setSelectedCollection(freshCollection);
    } else {
      console.warn(`⚠️ Could not reload ${collection.id}, using cached data`);
      setSelectedCollection(collection);
    }
  };

  const handleBackClick = () => {
    setSelectedCollection(null);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => {
      return file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png';
    });
    setUploadFiles(prev => [...prev, ...imageFiles]);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => {
      return file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png';
    });
    setUploadFiles(prev => [...prev, ...imageFiles]);
  };

  // Remove file from upload list
  const handleRemoveFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle upload
  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      alert('Please select at least one image file');
      return;
    }

    const collectionName = isNewCollection ? newCollectionName.trim() : selectedCollectionForUpload;

    if (!collectionName) {
      alert('Please enter a collection name or select an existing collection');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadStatus(null);

      const formData = new FormData();
      uploadFiles.forEach(file => {
        formData.append('images', file);
      });
      formData.append('collectionName', collectionName);
      formData.append('isNewCollection', isNewCollection);

      const response = await fetch(`${API_URL}/api/upload-items`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();

      setUploadProgress(100);
      setUploadStatus({
        type: 'success',
        message: data.message
      });

      console.log('✅ Upload successful. Server has auto-generated metadata.');

      // Server already regenerated metadata for this collection
      // Just refresh the collections list from server
      setTimeout(() => {
        setRefreshTrigger(Date.now());
        console.log('🔄 Refreshing collections from server...');
        // Close modal after success
        setTimeout(() => {
          handleCloseUploadModal();
        }, 2000);
      }, 500);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        type: 'error',
        message: error.message || 'Failed to upload files'
      });
    } finally {
      setUploading(false);
    }
  };

  // Open upload modal
  const handleOpenUploadModal = () => {
    setShowUploadModal(true);
    setUploadFiles([]);
    setSelectedCollectionForUpload('');
    setNewCollectionName('');
    setIsNewCollection(false);
    setUploadProgress(0);
    setUploadStatus(null);
  };

  // Close upload modal
  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadFiles([]);
    setSelectedCollectionForUpload('');
    setNewCollectionName('');
    setIsNewCollection(false);
    setUploadProgress(0);
    setUploadStatus(null);
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (selectedCollection) {
    return (
      <AdminContainer>
        <InventoryEditor
          collection={selectedCollection}
          onSave={() => setRefreshTrigger(prev => prev + 1)}
          onBack={handleBackClick}
        />
      </AdminContainer>
    );
  }

  const handleRetry = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  if (loading) {
    return (
      <AdminContainer>
        <LoadingContainer>Loading inventory...</LoadingContainer>
      </AdminContainer>
    );
  }

  if (error) {
    return (
      <AdminContainer>
        <ErrorContainer>
          <div>{error}</div>
          <RetryButton onClick={handleRetry}>Retry</RetryButton>
        </ErrorContainer>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminHeader>Inventory Management</AdminHeader>
      {user && (
        <div style={{ marginBottom: '15px', color: '#999', fontSize: '0.9rem' }}>
          Logged in as: <strong>{user.username}</strong>
        </div>
      )}
      <AdminControls>
        <div></div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <AdminButton onClick={() => window.location.href = '/'}>Back to Website</AdminButton>
          <AdminButton onClick={() => navigate('/admin/orders')}>View Orders</AdminButton>
          <AdminButton onClick={handleLogout}>Logout</AdminButton>
          <UploadButton onClick={handleOpenUploadModal}>Upload New Items</UploadButton>
          <HamburgerMenuWrapper data-menu>
            <HamburgerButton onClick={() => setMenuOpen(!menuOpen)}>
              <span></span>
              <span></span>
              <span></span>
            </HamburgerButton>
            <DropdownMenu $isOpen={menuOpen}>
              <MenuItem onClick={() => { setShowUserManagement(true); setMenuOpen(false); }}>
                User Management
              </MenuItem>
              <MenuItem onClick={() => { handleUpdateStructure(); setMenuOpen(false); }}>
                Update Inventory
              </MenuItem>
              <MenuItem onClick={() => { handleSetNotAvailable(); setMenuOpen(false); }}>
                Set All Items Not Available
              </MenuItem>
            </DropdownMenu>
          </HamburgerMenuWrapper>
        </div>
      </AdminControls>

      {showUploadModal && (
        <UploadModal onClick={handleCloseUploadModal}>
          <UploadModalContent onClick={(e) => e.stopPropagation()}>
            <UploadModalHeader>Upload New Items</UploadModalHeader>

            <UploadForm>
              <FormGroup>
                <Label>Collection Type</Label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={!isNewCollection}
                      onChange={() => setIsNewCollection(false)}
                    />
                    <span>Existing Collection</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={isNewCollection}
                      onChange={() => setIsNewCollection(true)}
                    />
                    <span>New Collection</span>
                  </label>
                </div>
              </FormGroup>

              {isNewCollection ? (
                <FormGroup>
                  <Label>New Collection Name</Label>
                  <Input
                    type="text"
                    placeholder="Enter collection name"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                  />
                </FormGroup>
              ) : (
                <FormGroup>
                  <Label>Select Collection</Label>
                  <Select
                    value={selectedCollectionForUpload}
                    onChange={(e) => setSelectedCollectionForUpload(e.target.value)}
                  >
                    <option value="">-- Select Collection --</option>
                    {collections.map(col => (
                      <option key={col.id} value={col.folder}>
                        {col.title}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              )}

              <FormGroup>
                <Label>Select Images (JPG, PNG)</Label>
                <FileInputWrapper
                  isDragging={isDragging}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <FileInput
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleFileSelect}
                  />
                  <div>
                    <p>Drag & drop images here or click to browse</p>
                    <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '5px' }}>
                      Files will be auto-renamed as 01.jpg, 02.jpg, etc.
                    </p>
                  </div>
                </FileInputWrapper>

                {uploadFiles.length > 0 && (
                  <FileList>
                    {uploadFiles.map((file, index) => (
                      <FileItem key={index}>
                        <span>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                        <RemoveFileButton onClick={() => handleRemoveFile(index)}>
                          Remove
                        </RemoveFileButton>
                      </FileItem>
                    ))}
                  </FileList>
                )}
              </FormGroup>

              {uploading && (
                <ProgressBar>
                  <ProgressFill progress={uploadProgress} />
                </ProgressBar>
              )}

              {uploadStatus && (
                <StatusMessage type={uploadStatus.type}>
                  {uploadStatus.message}
                </StatusMessage>
              )}

              <ModalActions>
                <AdminButton onClick={handleCloseUploadModal} disabled={uploading}>
                  Cancel
                </AdminButton>
                <UploadButton onClick={handleUpload} disabled={uploading}>
                  {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} file${uploadFiles.length !== 1 ? 's' : ''}`}
                </UploadButton>
              </ModalActions>
            </UploadForm>
          </UploadModalContent>
        </UploadModal>
      )}

      {showUserManagement && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}

      <CollectionsList>
        {collectionsWithStats.map((collection) => (
          <CollectionCard key={collection.id} onClick={() => handleCollectionClick(collection)}>
            <CollectionTitle>{collection.title}</CollectionTitle>
            <CollectionStats>
              <StatItem>
                <StatLabel>Available Items</StatLabel>
                <StatValue>{collection.availableItems} / {collection.totalItems}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Year</StatLabel>
                <StatValue>{collection.year}</StatValue>
              </StatItem>
            </CollectionStats>
          </CollectionCard>
        ))}
      </CollectionsList>

      {notification && (
        <Notification type={notification.type}>
          {notification.message}
        </Notification>
      )}
    </AdminContainer>
  );
}

export default Admin;